import { useState } from 'react';
import { PriorityPicker } from './PriorityPicker.js';
import { usePrioritySetter } from './usePrioritySetter.js';
import type { PriorityLevel } from './priority.js';

interface PrioritySelectorProps {
    patientId: string;
    // Server-known priority; the control layers an optimistic override on top.
    priority: PriorityLevel;
    align?: 'left' | 'right';
}

// Editable case priority (extreme asks for a reason) with an optimistic update; DetailPanel and CompletionCard.
export function PrioritySelector({ patientId, priority, align = 'right' }: PrioritySelectorProps) {
    // Optimistic priority override — instant feedback while the mutation settles.
    const [override, setOverride] = useState<PriorityLevel | null>(null);
    const [priorBeforeOverride, setPriorBeforeOverride] = useState<PriorityLevel>(priority);
    if (priority !== priorBeforeOverride) {
        setOverride(null);
        setPriorBeforeOverride(priority);
    }
    const displayPriority: PriorityLevel = override ?? priority;

    const { setPriority } = usePrioritySetter();

    const handleSelect = (level: PriorityLevel, reason?: string) => {
        setOverride(level);
        // On failure the server value never changes, so the prop-change reset above
        // won't fire — revert the optimistic override explicitly.
        void setPriority(patientId, level, reason).then((ok) => {
            if (!ok) setOverride(null);
        });
    };

    return <PriorityPicker value={displayPriority} onSelect={handleSelect} align={align} />;
}
